import React from 'react'
import { LowBalanceWarning } from '../lib/lowBalance'
import { formatDateShort, formatMoney } from '../lib/format'
import { fromISODate } from '../lib/dateRules'

interface Props {
  warning: LowBalanceWarning
  currencySymbol: string
  todayIso: string
  /** Opens the calendar on the low day. */
  onShow: () => void
  onDismiss: () => void
}

/**
 * A strip under the header when the combined balance is heading below the
 * warning level in the window ahead. It names the lowest point rather than the
 * first day under the line: that's the number to plan around.
 */
export default function LowBalanceBanner({ warning, currencySymbol, todayIso, onShow, onDismiss }: Props) {
  const when = warning.date === todayIso ? 'today' : `on ${formatDateShort(fromISODate(warning.date))}`
  return (
    <div
      role="status"
      className="px-4 sm:px-6 py-2 bg-rag-mediumBg border-b border-rag-medium/40 flex items-center justify-between gap-3"
    >
      <p className="text-sm text-ink min-w-0">
        <strong className="font-medium">Low balance ahead:</strong> your combined balance drops to{' '}
        <span className="font-mono font-tabular">{formatMoney(warning.balance, currencySymbol)}</span> {when}
        <span className="hidden sm:inline">
          , under your {formatMoney(warning.threshold, currencySymbol)} warning level
        </span>
        .
      </p>
      <div className="flex items-center gap-3 shrink-0">
        <button onClick={onShow} className="px-3 py-1 text-xs rounded-sm border border-ink/30 text-ink hover:bg-white/60">
          Show me
        </button>
        <button onClick={onDismiss} aria-label="Dismiss" className="text-ink/70 hover:text-ink">
          ×
        </button>
      </div>
    </div>
  )
}
